import {type ReactNode, useCallback, useRef} from "react";

import type {ThreeEvent} from "@react-three/fiber";
import {
  GameObjectType,
  type ShapeType,
  type TLevelObject,
  config,
} from "@repo/shared";
import * as THREE from "three";

import {useGameState} from "~/contexts/gamestate/useGameState";

import {SelectionContext, type SelectionContextType} from "./SelectionContext";
import {useKeyboardHandlers} from "./useKeyboardHandlers";

export function SelectionProvider({children}: {children: ReactNode}) {
  const {getInstanceTypes, getInstanceType, getFullInstanceTypes, add} =
    useGameState();

  const dragging = useRef<boolean>(false);
  const multiSelection = useRef<boolean>(false);

  // Keep the selectedObject ref as before.
  const selectedObject = useRef<THREE.Object3D | null>(null);
  const currentGameObjectType = useRef<GameObjectType>(GameObjectType.PLASTIC);

  // Instead of a THREE.Box3, we use an Object3D container to represent the selection's bounds.
  // This container's transform (position/scale) will match the unioned bounds.
  const selectionBoundingBox = useRef<THREE.Object3D | null>(null);

  // Dummy mesh used to calculate bounding boxes for objects that lack actual geometry.
  const dummy = useRef(new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1)));
  const internalCalcBox = useRef(new THREE.Box3());

  // Original helper function to calculate an object's bounding box using the dummy mesh.
  // This assumes that the dummy's BoxGeometry represents a unit box, which we then transform using the object's transforms.
  const calculateObjectBounds = useCallback((object: THREE.Object3D) => {
    dummy.current.scale.copy(object.scale);
    dummy.current.position.copy(object.position);
    dummy.current.quaternion.copy(object.quaternion);
    return internalCalcBox.current.setFromObject(dummy.current);
  }, []);

  // Internal accumulator box that holds the unioned bounds.
  const internalSelectionBox = useRef(new THREE.Box3());

  // New ref to actively maintain the total number of selected objects.
  const selectionCount = useRef<number>(0);

  // Updated helper function that takes no arguments.
  // It will update the selection container based on the current selection count:
  // - If exactly one object is selected, copy that object's transform.
  // - Otherwise, update from the unioned internalSelectionBox.
  const updateSelectionContainer = useCallback(() => {
    if (selectionCount.current === 0) {
      selectionBoundingBox.current = null;
      return;
    }
    if (selectionBoundingBox.current === null) {
      selectionBoundingBox.current = new THREE.Object3D();
    }
    if (selectionCount.current === 1 && selectedObject.current) {
      // When a single object is selected, simply copy its transform.
      selectionBoundingBox.current.position.copy(
        selectedObject.current.position,
      );
      selectionBoundingBox.current.rotation.copy(
        selectedObject.current.rotation,
      );
      selectionBoundingBox.current.scale.copy(selectedObject.current.scale);
    } else {
      // When more than one object is selected, update based on the unioned bounding box.
      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      internalSelectionBox.current.getCenter(center);
      internalSelectionBox.current.getSize(size);
      selectionBoundingBox.current.position.copy(center);
      selectionBoundingBox.current.scale.copy(size);
      // Reset rotation to keep the container axis–aligned.
      selectionBoundingBox.current.rotation.set(0, 0, 0);
    }
  }, []);

  // Instead of using THREE.Box3.setFromObject on the object (which doesn't work without geometry),
  // we use the original calculateObjectBounds helper.
  // This function updates our internal box and then applies those bounds to the container.
  const addToBoundingBox = useCallback(
    (object: THREE.Object3D) => {
      // Use the helper to get a bounding box for the object.
      internalSelectionBox.current.copy(calculateObjectBounds(object));
      if (selectionBoundingBox.current !== null) {
        internalSelectionBox.current.union(
          calculateObjectBounds(selectionBoundingBox.current),
        );
      }
      updateSelectionContainer();
    },
    [calculateObjectBounds, updateSelectionContainer],
  );

  // Recalculate the overall bounds for the current selection.
  // This clears the internal accumulator and rebuilds it from scratch.
  const recalculateBoundingBox = useCallback(() => {
    let count = 0;
    let lastSelected: THREE.Object3D | null = null;

    internalSelectionBox.current.makeEmpty();
    getInstanceTypes().forEach(({manager}) => {
      manager.getSelection().forEach((obj) => {
        count++;
        lastSelected = obj;
        const objectBounds = calculateObjectBounds(obj);
        if (internalSelectionBox.current.isEmpty()) {
          internalSelectionBox.current.copy(objectBounds);
        } else {
          internalSelectionBox.current.union(objectBounds);
        }
      });
    });

    // Update selection count.
    selectionCount.current = count;
    // If a single object is selected, store it.
    selectedObject.current = count === 1 ? lastSelected : null;
    updateSelectionContainer();
  }, [getInstanceTypes, calculateObjectBounds, updateSelectionContainer]);

  const clearSelection = useCallback(() => {
    getInstanceTypes().forEach(({manager}) => {
      manager.clearSelection();
    });
    selectionBoundingBox.current = null;
    selectedObject.current = null;
    selectionCount.current = 0;
  }, [getInstanceTypes]);

  const deleteSelected = useCallback(() => {
    getInstanceTypes().forEach(({manager}) => {
      const sorted = [...manager.selectedObjects].sort((a, b) => b - a);
      sorted.forEach((index) => manager.removeInstance(index));
    });
    selectionBoundingBox.current = null;
    selectedObject.current = null;
    selectionCount.current = 0;
  }, [getInstanceTypes]);

  // When applying transformations, we apply them to both the selected objects
  // and the selection container. This way, the container's transform is updated directly.
  const applyTransformToSelected = useCallback(
    (matrices: THREE.Matrix4 | THREE.Matrix4[]) => {
      const matrixArray = Array.isArray(matrices) ? matrices : [matrices];
      getInstanceTypes().forEach(({manager}) =>
        manager.applyTransform(matrixArray),
      );
      matrixArray.forEach(
        (matrix) =>
          selectionBoundingBox.current !== null &&
          selectionBoundingBox.current.applyMatrix4(matrix),
      );
    },
    [getInstanceTypes],
  );

  const isValidClick = useCallback(
    (event: ThreeEvent<MouseEvent>) =>
      event.instanceId !== undefined &&
      !dragging.current &&
      event.nativeEvent.button !== 2,
    [dragging],
  );

  // When an instance is selected, update the selection and use the bounds helper.
  const selectInstance = useCallback(
    (instanceId: number, shapeType: ShapeType) => {
      if (!multiSelection.current) clearSelection();
      selectionCount.current++;

      const currentInstanceType = getInstanceType(shapeType);
      currentInstanceType.manager.select(instanceId);

      const selected = currentInstanceType.manager.objects[instanceId]!;

      selectedObject.current = selectionCount.current === 1 ? selected : null;

      addToBoundingBox(selected);
    },
    [addToBoundingBox, clearSelection, getInstanceType],
  );

  const handleSelectClick = useCallback(
    (event: ThreeEvent<MouseEvent>, shapeType: ShapeType) => {
      if (!isValidClick(event)) return;
      selectInstance(event.instanceId!, shapeType);
      event.stopPropagation();
    },
    [isValidClick, selectInstance],
  );

  const handleUnselectClick = useCallback(
    (event: ThreeEvent<MouseEvent>, shapeType: ShapeType) => {
      if (!isValidClick(event)) return;

      const currentInstanceType = getInstanceType(shapeType);
      currentInstanceType.manager.unselect(event.instanceId!);

      recalculateBoundingBox();

      event.stopPropagation();
    },
    [getInstanceType, isValidClick, recalculateBoundingBox],
  );

  const selectArea = useCallback(
    (boundingBox: {minX: number; maxX: number; minY: number; maxY: number}) => {
      if (!multiSelection.current) clearSelection();
      const {minX, maxX, minY, maxY} = boundingBox;

      getFullInstanceTypes().forEach(([_shapeType, {manager}]) => {
        for (
          let instanceId = manager.objects.length - 1;
          instanceId >= 0;
          instanceId--
        ) {
          const obj = manager.objects[instanceId]!;
          const objPosition = new THREE.Vector2(obj.position.x, obj.position.y);

          if (
            objPosition.x >= minX &&
            objPosition.x <= maxX &&
            objPosition.y >= minY &&
            objPosition.y <= maxY
          ) {
            manager.select(instanceId);
          }
        }
      });

      recalculateBoundingBox();
    },
    [clearSelection, getFullInstanceTypes, recalculateBoundingBox],
  );

  const addAndSelect = useCallback(
    (objects: TLevelObject[]) => {
      const tempMultiSelection = multiSelection.current;
      if (objects.length > 1) {
        clearSelection();
        multiSelection.current = true;
      }
      add(objects).map((id, index) =>
        selectInstance(id, config.objectTypes[objects[index]!.type].shape),
      );
      multiSelection.current = tempMultiSelection;
    },
    [add, clearSelection, selectInstance],
  );

  useKeyboardHandlers(multiSelection);

  const value: SelectionContextType = {
    addAndSelect,
    dragging,
    multiSelection,
    clearSelection,
    applyTransformToSelected,
    selectInstance,
    handleSelectClick,
    handleUnselectClick,
    selectArea,
    selectionBoundingBox,
    deleteSelected,
    recalculateBoundingBox,
    selectedObject,
    currentGameObjectType,
  };

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
}
